import React, { useEffect, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS } from '../mock';

function Logo({ dark }) {
  return (
    <a href="#top" className="flex items-center select-none" aria-label="OnBrand Emails home" data-testid="navbar-logo-link">
      <img
        src={dark ? '/onbrand-logo-white.png' : '/onbrand-logo.png'}
        alt="OnBrand Emails"
        className="h-8 w-auto md:h-9"
        data-testid="navbar-logo"
      />
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    // Sample the ACTUAL background/media beneath the header (not just the section
    // colour) at three x-positions aligned with the logo, links and CTA.
    const luminance = (color) => {
      const m = color && color.match(/[\d.]+/g);
      if (!m) return 1;
      const [r, g, b, a] = m.map(Number);
      if (a === 0) return 1; // fully transparent, treat as light
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    };
    const bgLumAt = (x, y) => {
      let el = document.elementFromPoint(x, y);
      // Skip the fixed header / its children so we read the content beneath it.
      if (el && headerRef.current && headerRef.current.contains(el)) return null;
      while (el && el !== document.documentElement) {
        if (el.classList && el.classList.contains('ob-dark-section')) return 0;
        if (el.dataset && el.dataset.obDark === 'true') return 0;
        const cs = getComputedStyle(el);
        const c = cs.backgroundColor;
        if (c && c !== 'transparent' && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(c)) {
          return luminance(c);
        }
        el = el.parentElement;
      }
      return 1; // default light
    };
    const update = () => {
      setScrolled(window.scrollY > 24);
      // Sample just BELOW the real header height (it can change with layout),
      // so the point represents the content beneath the transparent header.
      const hb = headerRef.current ? headerRef.current.getBoundingClientRect().height : 72;
      const sampleY = hb + 10;
      const w = window.innerWidth;
      const cw = Math.min(1280, w);
      const left = (w - cw) / 2;
      const xs = [left + 45, Math.round(w / 2), left + cw - 60];
      let darkVotes = 0;
      let valid = 0;
      xs.forEach((x) => {
        const lum = bgLumAt(x, sampleY);
        if (lum === null) return;
        valid += 1;
        if (lum < 0.5) darkVotes += 1;
      });
      if (valid > 0) setDark(darkVotes > valid / 2);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Light scrolled state gets a subtle cream blur; dark sections stay fully transparent
  const headerBg =
    scrolled && !dark
      ? 'bg-[var(--ob-bg)]/85 backdrop-blur-md border-b border-[var(--ob-line)]'
      : 'bg-transparent border-b border-transparent';

  const linkColor = dark ? 'text-white/90 hover:text-white' : 'text-[var(--ob-ink)]';
  const ctaClass = dark
    ? 'bg-[var(--ob-accent)] text-[var(--ob-dark)] hover:bg-white'
    : 'bg-[var(--ob-ink)] text-white hover:bg-[var(--ob-dark)]';

  // Smooth-scroll to an anchor with correction for lazy-loaded layout shifts,
  // so far-down sections (e.g. the Design Studio) always land under the sticky header.
  const scrollToId = (id) => {
    const OFFSET = 88;
    const go = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - OFFSET;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };
    go();
    // Re-target after images below settle and once the animation finishes.
    setTimeout(go, 650);
    setTimeout(go, 1300);
  };
  const handleNavClick = (e, href) => {
    if (!href || !href.startsWith('#')) return;
    e.preventDefault();
    scrollToId(href.slice(1));
  };

  return (
    <header ref={headerRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}>
      <nav className="mx-auto flex max-w-[1280px] items-center justify-between px-5 py-4 md:px-8">
        <Logo dark={dark} />

        <ul className="hidden items-center gap-6 lg:flex xl:gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a href={l.href} onClick={(e) => handleNavClick(e, l.href)} className={`ob-navlink text-sm font-medium transition-colors duration-300 ${linkColor}`}>
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <a
            href="https://calendly.com/hello-onbrandadmin/30min" target="_blank" rel="noopener noreferrer"
            className={`ob-btn inline-flex items-center rounded-full px-8 py-3.5 text-base font-semibold transition-colors duration-300 ${ctaClass}`}
          >
            Book A Free Audit
          </a>
        </div>

        <button
          className={`lg:hidden transition-colors duration-300 ${dark ? 'text-white' : 'text-[var(--ob-ink)]'}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-[var(--ob-line)] bg-[var(--ob-bg)] px-5 py-4">
          <ul className="flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => { setOpen(false); handleNavClick(e, l.href); }}
                  className="block text-base font-medium text-[var(--ob-ink)]"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href="https://calendly.com/hello-onbrandadmin/30min" target="_blank" rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-[var(--ob-ink)] px-8 py-4 text-base font-semibold text-white"
              >
                Book A Free Audit
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
